// src/pages/reset-password/ResetPasswordPage.tsx

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { resetPassword } from "../../services/passwordService";
import { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH } from "../../constants";
import "./ResetPasswordPage.css";

/**
 * Full-page password reset form.
 * Route: /sifre-sifirla?token={uuid}
 *
 * Five states:
 *   "form"    — default: new password + confirm inputs
 *   "loading" — request in flight
 *   "success" — password changed, prompt user to log in
 *   "expired" — token was valid once but has passed its 1-hour window
 *   "invalid" — token not found or already consumed
 */
type ResetState = "form" | "loading" | "success" | "expired" | "invalid";

export const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") ?? "";

  const [state, setState] = useState<ResetState>(token ? "form" : "invalid");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");

  const goHome = () => {
    window.location.href = "/";
  };

  const validate = (): boolean => {
    let valid = true;

    if (!newPassword) {
      setPasswordError(t("reset_error_password_required"));
      valid = false;
    } else if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setPasswordError(t("reset_error_password_min", { min: PASSWORD_MIN_LENGTH }));
      valid = false;
    // Upper bound mirrors the backend so the rule fails here, not after submit
    } else if (newPassword.length > PASSWORD_MAX_LENGTH) {
      setPasswordError(t("reset_error_password_max", { max: PASSWORD_MAX_LENGTH }));
      valid = false;
    } else {
      setPasswordError("");
    }

    if (!confirmPassword) {
      setConfirmError(t("reset_error_confirm_required"));
      valid = false;
    } else if (newPassword !== confirmPassword) {
      setConfirmError(t("reset_error_passwords_mismatch"));
      valid = false;
    } else {
      setConfirmError("");
    }

    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setState("loading");
    try {
      await resetPassword(token, newPassword);
      setState("success");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("RESET_TOKEN_EXPIRED")) {
        setState("expired");
      } else if (msg.startsWith("PASSWORD_TOO_SHORT:")) {
        // The token is fine — only the password broke a rule, so stay on the
        // form and show the reason instead of burning the reset link
        setPasswordError(t("PASSWORD_TOO_SHORT", { min: msg.split(":")[1] }));
        setState("form");
      } else if (msg.startsWith("PASSWORD_TOO_LONG:")) {
        setPasswordError(t("PASSWORD_TOO_LONG", { max: msg.split(":")[1] }));
        setState("form");
      } else if (msg === "PASSWORD_REQUIRED") {
        setPasswordError(t("reset_error_password_required"));
        setState("form");
      } else {
        setState("invalid");
      }
    }
  };

  /* ── Render helpers ─────────────────────────────────────── */

  const renderSuccess = () => (
    <>
      <div className="reset-icon">✅</div>
      <h1 className="reset-title">{t("reset_success_title")}</h1>
      <p className="reset-text">{t("reset_success_text")}</p>
      <button className="reset-btn" onClick={goHome}>
        {t("reset_success_btn")}
      </button>
    </>
  );

  const renderExpired = () => (
    <>
      <div className="reset-icon">⏰</div>
      <h1 className="reset-title">{t("reset_expired_title")}</h1>
      <p className="reset-text">{t("reset_expired_text")}</p>
      <button className="reset-btn" onClick={goHome}>
        {t("reset_expired_btn")}
      </button>
    </>
  );

  const renderInvalid = () => (
    <>
      <div className="reset-icon">❌</div>
      <h1 className="reset-title">{t("reset_invalid_title")}</h1>
      <p className="reset-text">{t("reset_invalid_text")}</p>
      <button className="reset-btn" onClick={goHome}>
        {t("reset_invalid_btn")}
      </button>
    </>
  );

  const renderForm = () => (
    <>
      <h1 className="reset-title">{t("reset_form_title")}</h1>
      <p className="reset-text reset-text--form">{t("reset_form_subtitle")}</p>

      <form onSubmit={handleSubmit} className="reset-form" noValidate>
        <div className="reset-form-group">
          <label htmlFor="rp-password">{t("reset_password_label")}</label>
          <input
            id="rp-password"
            type="password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setPasswordError("");
            }}
            className={passwordError ? "input-error" : ""}
            placeholder={t("reset_password_placeholder")}
            disabled={state === "loading"}
            autoFocus
          />
          {/* Rules are stated up-front, not only after a failed submit.
              NOTE: no maxLength attribute on purpose — see RegisterModal.tsx */}
          <span className={`reset-field-hint${newPassword.length > PASSWORD_MAX_LENGTH ? " reset-field-hint--over" : ""}`}>
            {t("password_rules_hint", { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH })}
            {newPassword.length > 0 && ` (${newPassword.length}/${PASSWORD_MAX_LENGTH})`}
          </span>
          {passwordError && (
            <span className="reset-field-error">{passwordError}</span>
          )}
        </div>

        <div className="reset-form-group">
          <label htmlFor="rp-confirm">{t("reset_confirm_label")}</label>
          <input
            id="rp-confirm"
            type="password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setConfirmError("");
            }}
            className={confirmError ? "input-error" : ""}
            placeholder={t("reset_confirm_placeholder")}
            disabled={state === "loading"}
          />
          {confirmError && (
            <span className="reset-field-error">{confirmError}</span>
          )}
        </div>

        <button
          type="submit"
          className="reset-submit-btn"
          disabled={state === "loading"}
        >
          {state === "loading" ? t("reset_submitting") : t("reset_submit")}
        </button>
      </form>

      <div className="reset-footer">
        <button type="button" className="reset-link-btn" onClick={goHome}>
          {t("reset_back_home")}
        </button>
      </div>
    </>
  );

  const renderContent = () => {
    switch (state) {
      case "success":
        return renderSuccess();
      case "expired":
        return renderExpired();
      case "invalid":
        return renderInvalid();
      case "loading":
      case "form":
      default:
        return renderForm();
    }
  };

  return (
    <div className="reset-page">
      <div className="reset-card">
        <div className="reset-site-name">3 Korner 1 Penaltı</div>
        {renderContent()}
      </div>
    </div>
  );
};